import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Loader } from 'semantic-ui-react'
import { useReselector } from '@/utils/sharedHooks'
import {
  Button,
  Text,
  Alert,
  Modal,
  Popup,
} from '@/componentsV1/BaseComponents'
import {
  getIsAdministrator,
  selectIsBookkeeper,
  selectIsAccountingOpsManager,
} from '@/selectors/user.selectors'
import { useAppDispatch } from '@/utils/typeHelpers'
import {
  adminRequestCubkFollowup,
  adminFetchUserCubkStatus,
  type CatchUpBookkeepingStatus,
} from '@/components/CatchUpBookkeepingStatus/catchUpBookkeepingStatus.slice'
import { DateTime } from 'luxon'
import { DATE_FORMATS_LUXON } from '@/utils/dateHelpers'
import {
  fetchFollowupEligibility,
  type FollowupEligibility,
} from '@/actions/admin/v2/adminCubkActions'
import './RequestCubkFollowupButton.scss'

export interface RequestCubkFollowupUser {
  id: number
  firstName: string
  lastName: string
}

const RequestCubkFollowupConfirmationModal = ({
  user,
  open,
  close,
  onSuccess,
}: {
  user: RequestCubkFollowupUser
  open: boolean
  close: () => void
  onSuccess?: (data: CatchUpBookkeepingStatus) => void
}) => {
  const dispatch = useAppDispatch()
  const [errorMessage, setErrorMessage] = useState('')
  const [submitting, setSubmitting] = useState(false)

  const submitButtonContent = useMemo(() => {
    if (!submitting) {
      return 'Request Followup'
    }
    return <Loader active inline size="small" />
  }, [submitting])

  const handleConfirm = async () => {
    setSubmitting(true)
    setErrorMessage('')

    const result = await adminRequestCubkFollowup(user.id)(dispatch)

    if (result) {
      close()
      onSuccess?.(result)
    } else {
      setErrorMessage('Failed to request followup. Please try again.')
    }

    setSubmitting(false)
  }

  return (
    <Modal size="small" open={open} onClose={close} closeIcon>
      <Modal.Header>Confirm followup request</Modal.Header>
      <Modal.Content>
        {errorMessage && <Alert type="error">Error: {errorMessage}</Alert>}
        <Text>
          This will display a followup request on this user&apos;s dashboard.
        </Text>
        <br />
        <Text>
          Continue only if you are certain you have completed as much of their
          catch-up bookkeeping as you can without additional clarification
          and/or uploaded statements.
        </Text>
      </Modal.Content>
      <Modal.Actions>
        <div className="confirmFollowupActions">
          <Button variant="secondary" onClick={close}>
            Cancel
          </Button>
          <Button disabled={submitting} onClick={handleConfirm}>
            {submitButtonContent}
          </Button>
        </div>
      </Modal.Actions>
    </Modal>
  )
}

const RequestCubkFollowupButton = ({
  user,
  cubkStatus: cubkStatusProp,
  onFollowupRequested,
}: {
  user: RequestCubkFollowupUser
  cubkStatus?: CatchUpBookkeepingStatus | null
  onFollowupRequested?: (data: CatchUpBookkeepingStatus) => void
}) => {
  const dispatch = useAppDispatch()
  const isAdministrator = useReselector(getIsAdministrator)
  const isBookkeeper = useReselector(selectIsBookkeeper)
  const isAccountingOpsManager = useReselector(selectIsAccountingOpsManager)
  const [modalOpen, setModalOpen] = useState(false)
  const [fetchedCubkStatus, setFetchedCubkStatus] =
    useState<CatchUpBookkeepingStatus | null>(null)
  const [loading, setLoading] = useState(false)
  const [eligibility, setEligibility] = useState<FollowupEligibility | null>(
    null
  )
  const [eligibilityLoading, setEligibilityLoading] = useState(false)

  // Use provided CUBK status or fetched one
  const cubkStatus = cubkStatusProp ?? fetchedCubkStatus

  // Fetch CUBK status if not provided as prop
  useEffect(() => {
    let cancelled = false
    if (cubkStatusProp === undefined) {
      const load = async () => {
        setLoading(true)
        const result = await adminFetchUserCubkStatus(user.id)(dispatch)
        if (!cancelled) {
          setFetchedCubkStatus(result ?? null)
          setLoading(false)
        }
      }
      load()
    }
    return () => {
      cancelled = true
    }
  }, [cubkStatusProp, dispatch, user.id])

  const isAuthorized = isBookkeeper || isAdministrator || isAccountingOpsManager
  const phase = cubkStatus?.phase

  // While the phase is your_review, poll every 30 s so the CTA re-appears
  // automatically once the user completes their tasks — without a page reload.
  // Polling is scoped to your_review only; other phases need no background updates.
  const pollIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null)

  useEffect(() => {
    // Only poll when the component owns the fetch (no prop provided) and we
    // are actively waiting on the user.
    if (cubkStatusProp === undefined && phase === 'your_review') {
      pollIntervalRef.current = setInterval(async () => {
        const result = await adminFetchUserCubkStatus(user.id)(dispatch)
        setFetchedCubkStatus(result ?? null)
      }, 30_000)
    } else if (pollIntervalRef.current !== null) {
      clearInterval(pollIntervalRef.current)
      pollIntervalRef.current = null
    }

    return () => {
      if (pollIntervalRef.current !== null) {
        clearInterval(pollIntervalRef.current)
        pollIntervalRef.current = null
      }
    }
  }, [cubkStatusProp, dispatch, phase, user.id])

  // Reset eligibility when phase changes away from bk_review so that when we
  // re-enter bk_review (after a your_review cycle), the eligibility fetch
  // starts from a clean state rather than briefly showing a stale null value.
  useEffect(() => {
    if (phase !== 'bk_review') {
      setEligibility(null)
    }
  }, [phase])

  // Fetch eligibility only while a CTA could be shown — i.e. authorized viewer
  // looking at a user in bk_review. Other phases either hide the CTA
  // (complete/bank_incompatible) or show the timestamp instead (your_review),
  // so eligibility is irrelevant.
  useEffect(() => {
    let cancelled = false
    if (isAuthorized && phase === 'bk_review') {
      const load = async () => {
        setEligibilityLoading(true)
        try {
          const result = await fetchFollowupEligibility(user.id)
          if (!cancelled) setEligibility(result)
        } catch {
          // Fail open — backend 422 is the safety net
          if (!cancelled)
            setEligibility({
              eligible: true,
              missingStatements: false,
              clarificationTransactions: 0,
            })
        } finally {
          if (!cancelled) setEligibilityLoading(false)
        }
      }
      load()
    }
    return () => {
      cancelled = true
    }
  }, [isAuthorized, phase, user.id])

  const handleSuccess = useCallback(
    (data: CatchUpBookkeepingStatus) => {
      setFetchedCubkStatus(data)
      onFollowupRequested?.(data)
    },
    [onFollowupRequested]
  )

  if (loading || eligibilityLoading) {
    return null
  }

  // Hidden when CUBK complete, no status, or bank incompatible
  if (!cubkStatus || phase === 'complete' || phase === 'bank_incompatible') {
    return null
  }

  // Show timestamp while a follow-up round is in progress (user has pending tasks)
  if (phase === 'your_review' && cubkStatus.followupRequestedAt) {
    const formattedDate = DateTime.fromISO(
      cubkStatus.followupRequestedAt
    ).toFormat(DATE_FORMATS_LUXON.DISPLAY_SHORT)
    return (
      <Text as="bodyXs" color="darkGray">
        Followup Requested {formattedDate}
      </Text>
    )
  }

  const buttonDisabled = !isAuthorized || !eligibility?.eligible
  const noItemsTooltip =
    isAuthorized && !eligibility?.eligible
      ? 'No outstanding items to request followup for'
      : undefined

  return (
    <>
      <Popup
        disabled={!noItemsTooltip}
        content={noItemsTooltip}
        trigger={
          <span>
            <Button
              onClick={() => setModalOpen(true)}
              disabled={buttonDisabled}
              variant="link"
              inline
              size="small"
              aria-label={`Request catch-up bookkeeping followup for ${user.firstName} ${user.lastName}`}
            >
              Request CUBK Followup →
            </Button>
          </span>
        }
      />
      {modalOpen && (
        <RequestCubkFollowupConfirmationModal
          user={user}
          open={modalOpen}
          close={() => setModalOpen(false)}
          onSuccess={handleSuccess}
        />
      )}
    </>
  )
}

export default RequestCubkFollowupButton
